import type { ProgrammaticLandingPageConfig } from '../types'
import { KWP_SIZE_FACTS } from './kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[12]

export const KWP_12_PHOTOVOLTAIK_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '12-kwp-photovoltaik-kosten',
  lang: 'de',
  topicId: 'kwp-kosten-12',
  title: '12 kWp Photovoltaikanlage: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet eine 12-kWp-Photovoltaikanlage? Dachfläche, Modulanzahl, Kostenaufschlüsselung und ' +
    'kostenloser Preisvergleich für Häuser mit Wärmepumpe.',
  keywords: [
    '12 kWp Photovoltaik Kosten',
    '12 kWp Solaranlage Preis',
    'Photovoltaikanlage 12 kWp',
    'Was kostet eine 12 kWp Anlage',
    '12 kWp PV-Anlage Wärmepumpe',
  ],
  eyebrow: 'Anlagengröße 12 kWp',
  h1: '12 kWp Photovoltaikanlage: Kosten und Richtwerte',
  intro:
    'Eine 12-kWp-Anlage eignet sich für größere Einfamilienhäuser, oft bereits mit Wärmepumpe. ' +
    'Hier finden Sie Richtwerte zu Dachfläche und Modulanzahl, eine Kostenaufschlüsselung und ' +
    'direkt darunter einen Rechner mit dem für 12 kWp vorbelegten Durchschnittspreis aus echten ' +
    'Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp', kwp: 12 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich eine 12-kWp-Anlage?' },
    {
      type: 'paragraph',
      text:
        'Richtwert: 12 kWp passen typischerweise zu einem größeren Einfamilienhaus mit einer ' +
        'Wärmepumpe als zusätzlichem Stromverbraucher, Jahresverbrauch insgesamt oft im Bereich ' +
        'von 7.000-9.000 kWh. Ohne Wärmepumpe reicht für die meisten Haushalte bereits eine ' +
        'kleinere Stufe wie 8 oder 10 kWp.',
    },
    { type: 'heading', text: 'Dachfläche und Module: das braucht eine 12-kWp-Anlage' },
    {
      type: 'paragraph',
      text:
        `Richtwert: rund ${FACTS.roofAreaM2} m² Dachfläche und ca. ${FACTS.moduleCount} Module bei ` +
        `marktüblichen ~440-Wp-Modulen. Der Jahresertrag liegt bei Südausrichtung ohne Verschattung ` +
        `bei etwa ${FACTS.annualYieldKwh.toLocaleString('de-DE')} kWh. Eine genaue, dachspezifische ` +
        'Berechnung liefert der kostenlose PV-Rechner.',
    },
    { type: 'heading', text: 'Kostenbestandteile einer Photovoltaikanlage' },
    {
      type: 'list',
      items: [
        'Module: größter Einzelposten, meist rund ein Drittel der Gesamtkosten',
        'Wechselrichter: bei 12 kWp häufig bereits ein dreiphasiges Modell nötig',
        'Montage/Arbeitsleistung: Befestigung auf dem Dach, Verkabelung',
        'Gerüst: abhängig von Dachhöhe und -zugänglichkeit',
        'Elektroinstallation: Zählerschrank-Anpassung, oft mit Blick auf die Wärmepumpe',
        'Anmeldung: Netzbetreiber und Marktstammdatenregister',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Bei Anlagen ab etwa 12 kWp lohnt sich ein genauerer Blick auf den Hausanschluss - je nach ' +
        'vorhandener Absicherung kann ein dreiphasiger Wechselrichter nötig werden, was die ' +
        'Elektroinstallation etwas aufwendiger macht als bei kleineren Anlagen.',
    },
    { type: 'heading', text: 'Amortisation und Eigenverbrauch' },
    {
      type: 'paragraph',
      text:
        'Mit Wärmepumpe als zusätzlichem Verbraucher lässt sich der erzeugte Strom oft auch ohne ' +
        'großen Batteriespeicher gut selbst nutzen, insbesondere wenn die Wärmepumpe gezielt tagsüber ' +
        'läuft. Details zu Einspeisevergütung und Förderungen im Förder-Überblick, eine genaue ' +
        'Berechnung für Ihr Dach liefert der PV-Rechner.',
    },
  ],
  faq: [
    {
      q: 'Reicht 12 kWp für Wärmepumpe und Elektroauto zusammen?',
      a: 'Das hängt vom genauen Verbrauch beider Geräte ab - bei sparsamer Wärmepumpe und moderater E-Auto-Nutzung oft ja, bei hohem Gesamtverbrauch ist 15 kWp die sicherere Wahl.',
    },
    {
      q: 'Wie groß muss das Dach für 12 kWp mindestens sein?',
      a: `Richtwert: rund ${FACTS.roofAreaM2} m² nutzbare Dachfläche, abhängig vom gewählten Modultyp und der Ausrichtung.`,
    },
    {
      q: 'Brauche ich bei 12 kWp einen Dreiphasen-Wechselrichter?',
      a: 'Häufig ja, abhängig vom vorhandenen Hausanschluss. Das prüft der Handwerksbetrieb im Rahmen des Angebots anhand Ihres Zählerschranks.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '10 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/10-kwp-photovoltaik-kosten' },
    { label: '15 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/15-kwp-photovoltaik-kosten' },
    { label: '20 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/20-kwp-photovoltaik-kosten' },
    { label: '12 kWh Batteriespeicher: Kosten & Richtwerte', href: '/12-kwh-speicher-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'Photovoltaik Schritt für Schritt planen', href: '/photovoltaik-planen' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
